import { type RefObject, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Move focus to the page's main heading: the first `h1` inside `main`, or `main` itself when the
 * page has none. The target is made focusable by script only (`tabindex="-1"`, so it does not join
 * the Tab order) unless it already carries a `tabindex` of its own. The target is a reading
 * position, not a control, so it draws no focus ring: AppLayout.module.css drops the global
 * `:focus-visible` ring for `tabindex="-1"` targets inside `<main>`, and every control keeps it.
 */
export function focusPageHeading(main: HTMLElement): void {
    const target = main.querySelector<HTMLElement>('h1') ?? main;
    if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
    }
    target.focus();
}

export interface PageHeadingFocusProps {
    /**
     * Set by the layout when a nav choice in the tablet drawer closed it. Read and cleared on the
     * next location this component commits with, so a later navigation made some other way does not
     * move focus.
     */
    readonly pending: RefObject<boolean>;
    /** The `<main>` the routed page renders into. */
    readonly mainRef: RefObject<HTMLElement | null>;
}

/**
 * After a nav choice closes the tablet drawer, focus lands on the new page's main heading (WCAG
 * 2.4.3) rather than going back to the menu toggle: the person chose a destination, so the next
 * thing to read is that destination.
 *
 * Rendered INSIDE the layout's Suspense boundary, beside the routed `<Outlet/>`, which is what makes
 * it generic across pages: it commits in the same commit as the page it sits beside. While a lazily
 * loaded page is still suspended, this component's update for the new location is held back with
 * it, so the effect below runs once the page's heading is in the DOM — never against the loading
 * skeleton or the page being left. A layout effect, so focus moves before the browser paints.
 */
export function PageHeadingFocus({ pending, mainRef }: PageHeadingFocusProps) {
    const { key } = useLocation();
    // `null`, not the current key: this component can first commit AT the destination of a drawer
    // choice — when the page open before it never left the Suspense fallback (a deep link to a lazy
    // route that had not loaded) — and that first commit must still consume the request. Seeding
    // the key here would skip it, leave the request pending, and move focus on the NEXT navigation
    // instead. On an ordinary first load the request is not set, so the first run moves nothing.
    const handledKey = useRef<string | null>(null);

    useLayoutEffect(() => {
        // The same location re-running this effect (Suspense re-showing hidden content re-runs
        // layout effects) is not a navigation.
        if (key === handledKey.current) {
            return;
        }
        handledKey.current = key;
        if (!pending.current) {
            return;
        }
        pending.current = false;
        if (mainRef.current !== null) {
            focusPageHeading(mainRef.current);
        }
    }, [key, pending, mainRef]);

    return null;
}
